import { Component, HostBinding, OnInit, inject } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { Option } from '@fmnts/core';
import { getUnique } from '@fmnts/core/collection';
import {
  distinctUntilArrayItemChanged,
  distinctUntilFormKeysChanged,
} from '@fmnts/core/rxjs';
import {
  AbstractDeclarativeInputFieldComponent,
  ConditionalService,
  FormSelectField,
  SelectInputItem,
} from '@fmnts/declarative-forms';
import {
  filterSelectItems,
  toItemsWithCondition,
} from '@fmnts/declarative-forms/common';
import { faSquare, faSquareCheck } from '@fortawesome/pro-regular-svg-icons';
import { Observable, debounceTime, map, of, startWith, tap } from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';

type Item = SelectInputItem;
type FieldValue = Option<readonly string[]>;

/**
 * Component that displays a group of checkboxes
 */
@Component({
  selector: 'next-checkbox-group-field',
  templateUrl: './checkbox-group-field.component.html',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule],
})
export class CheckboxGroupFieldComponent
  extends AbstractDeclarativeInputFieldComponent<FormSelectField, FieldValue>
  implements ControlValueAccessor, OnInit
{
  private readonly conditionalService = inject(ConditionalService);

  @HostBinding('class') componentClass =
    'form-field form-field__checkbox-group';

  /** Icon to use, if state is checked */
  public readonly iconChecked = faSquareCheck;
  /** Icon to use, if state is unchecked */
  public readonly iconUnchecked = faSquare;

  public items$!: Observable<Item[]>;

  // an array of those checkbox keys ("values" in the backend) in the group that are set to true
  private _value: FieldValue = [];

  ngOnInit(): void {
    this.items$ = this.getItems$();
  }

  get value(): readonly string[] {
    if (!this._value) {
      return [];
    }
    return this._value;
  }

  set value(value: FieldValue) {
    this.updateValue(value);
    this._onTouched();
  }

  writeValue(newValue) {
    this._value = newValue;
  }

  updateValue(newValue: FieldValue) {
    this._value = newValue;
    this._onChange(this._value);
  }

  public toggleChecked(item: Item) {
    // Do nothing, if item is still null
    if (item === null) {
      return;
    }
    if (this.value.includes(item.value)) {
      this.value = this.value.filter((el) => el !== item.value);
    } else {
      this.value = [...this.value, item.value];
    }
  }

  public isChecked(item: Item) {
    return this.value.includes(item.value);
  }

  get showError(): boolean {
    const { field, form, control } = this;
    if (!field || !form) {
      return false;
    }

    return this.value && control.touched && control.invalid;
  }

  /**
   * @returns
   * Observable for items
   */
  private getItems$(): Observable<Item[]> {
    if (!this.field) {
      // No items without a field
      return of([]);
    }

    const { items, itemConditions = {} } = this.field.widgetOptions;
    const itemsWithConditions = toItemsWithCondition(items, itemConditions);
    // First extract all relevant field keys
    const observableKeys = getUnique(
      itemsWithConditions.flatMap(({ condition }) =>
        this.conditionalService.extractReferencedKeys(condition),
      ),
    );

    if (observableKeys.length === 0) {
      // When there are no keys to observe, the items won't
      // change over time
      return of(items);
    }

    const changes$ = this.form.flatValueChanges.pipe(
      debounceTime(100),
      startWith(this.form.flatValue),
      distinctUntilFormKeysChanged(observableKeys),
    );

    return of(itemsWithConditions).pipe(
      filterSelectItems(
        () => changes$,
        (changes, { condition }) =>
          condition
            ? this.conditionalService.checkObj(changes, condition)
            : true,
      ),
      map((filtered) => filtered.map(({ item }) => item)),
      distinctUntilArrayItemChanged(
        (prev, current) => prev.value === current.value,
      ),
      tap((filteredItems) => this.unselectItems(filteredItems)),
    );
  }

  /**
   * Unselects all checkboxes as soon as the condition is not fulfilled anymore.
   *
   * @param items The available items
   */
  private unselectItems(items: Item[]) {
    const selectedItems = this.value.filter((x) =>
      items.some((y) => y.value === x),
    );

    if (this.value.length !== selectedItems.length) {
      this.updateValue(selectedItems);
    }
  }
}
